import React, { useEffect } from 'react';
import { sortBy, keyBy, groupBy, range } from 'lodash';
import { format as formatDate, addHours, set as setDate } from 'date-fns';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import classnames from 'classnames';
import ja from 'date-fns/locale/ja';
import { toast } from 'react-toastify';
import { Button, } from 'reactstrap';
import qs from 'qs';

import firebase from '../../firebase';
import AdminPage from '../hocs/AdminPage';
import useAppTitle from '../hooks/useAppTitle';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useTenant from '../hooks/useTenant';
import useQueryParams from '../hooks/useQueryParams';
import LectureSelector from '../LectureSelector';
import TenantLink from '../TenantLink';
import LectureDetailModal from '../modals/LectureDetailModal';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import ModalButton from '../ModalButton';
import PicsFormModal from '../modals/PicsFormModal';
import FilesUploadButton from '../FilesUploadButton';
import { staffRoles } from '../../shared/config';
import LectureImagesManager from '../LectureImagesManager';

const db = firebase.firestore();
const usersRef = db.collection('users');
const eventsRef = db.collection('events');

export default AdminPage(function AdminLecture(props) {
  const {
    match: {
      params: { eventId, lectureId },
    },
    addBreadNavValues,
  } = props;
  const eventRef = eventsRef.doc(eventId);
  const event = useDocumentSubscription(eventRef, [eventId]);
  const lectures = useCollectionSubscriptionInTenant(eventRef.collection('lectures'), [eventId]);
  const lecture = lectures.find(_ => _.id === lectureId);
  const lectureFrameImagesRef = eventRef.collection('lectureFrameImages').doc(lectureId);
  const lectureFrameImagesDoc = useDocumentSubscription(lectureFrameImagesRef, [eventId, lectureId]);
  useAppTitle(['ADMIN', event?.name, (_ => _ && formatDate(_, 'yyyy/MM/dd'))(lecture?.date.toDate()), 'スケジュール'].join(' '));
  const eventProductTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('eventProductTypes')), _ => _.createdAt.toDate());
  const lectureTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('lectureTypes')), _ => _.createdAt.toDate());
  const eventProductTypesById = keyBy(eventProductTypes, 'id');
  const lectureTypesById = keyBy(lectureTypes, 'id');
  const entries = useCollectionSubscription(eventRef.collection('entries').where('lectureIds', 'array-contains', lectureId).orderBy('createdAt'), [eventId, lectureId]);
  const entryFrames = entries
    .map((e) => e.frames.map((_) => ({ ..._, entry: e })))
    .reduce((x, y) => [...x, ...y], [])
    .filter(_ => _.lectureId === lectureId);
  const entryFramesGroupedByFrameIndex = groupBy(
    entryFrames,
    'frameIndex'
  );
  const { frames: lectureFrames = [] } = lecture || {};
  const staffs = useCollectionsFetchInTenant(['admin', 'staff'].map(_ => usersRef.where('role', '==', _)));
  useEffect(() => {
    addBreadNavValues({ event, lecture });
  }, [event, lecture]);

  const entryIds = lectureFrames
    .map((lectureFrame, frameIndex) => {
      const { capacity = 0 } = lectureFrame;
      const entryFrames = entryFramesGroupedByFrameIndex[frameIndex] || [];
      return [
        ...range(0, capacity).map((seatIndex) => {
          const { entry } = entryFrames.filter((_) => _.entry?.cancelledAt == null)[seatIndex] || {};
          return entry?.id;
        }),
        ...entryFrames
          .filter((_) => _.entry?.cancelledAt != null)
          .map((entryFrame, i) => {
            const { entry } = entryFrame;
            return entry?.id;
          }),
      ];
    })
    .flat()
    .filter((_) => _);

  const handleStartSendingImages = async () => {
    const outsideFrameImagesCount = (lectureFrameImagesDoc?.images || []).filter(_ => _.frameIndex === -1).length;
    if (outsideFrameImagesCount > 0) {
      if (!window.confirm(`⚠️ 枠外の画像が${outsideFrameImagesCount}枚あります。\n\n枠外の画像はどの時間枠にも振り分けられていないため、誰にも送信されません。\n本来いずれかの時間枠に入るべき画像であれば、先に「画像」管理画面で正しい枠へ移動してください。\n\nこのまま画像送信を開始してもよろしいですか？`)) {
        return;
      }
    }

    if (!window.confirm('画像送信を開始しますか？\n\nこれまでのアンケート回答者全員に画像が送信され、以降は自動的に送信されるようになります。')) {
      return;
    }

    try {
      await lecture.ref.update({ imagesSendingStarted: true });

      const entriesToUpdate = entries.filter(_ =>
        _.frames?.some(frame =>
          frame.lectureId === lectureId && frame.laterAnswers
        )
      );

      await Promise.all(
        entriesToUpdate.map(_ =>
          _.ref.update({ shouldSendImages: true })
        )
      );

      // NOTE: 事後アンケート未回答者にはアンケート回答を促すメールを送る
      const entriesToRemind = entries.filter(_ =>
        _.cancelledAt == null &&
        _.abortedAt == null &&
        _.frames?.some(frame =>
          frame.lectureId === lectureId && !frame.laterAnswers
        )
      );

      await Promise.all(
        entriesToRemind.map(_ =>
          _.ref.update({ shouldSendLaterQuestionInstruction: true })
        )
      );

      toast.success(`画像送信を開始しました（回答者${entriesToUpdate.length}件に画像送信、未回答者${entriesToRemind.length}件にアンケート促しメール送信）`);
    } catch (e) {
      console.error(e);
      toast.error('画像送信の開始に失敗しました');
    }
  };

  const handleStopSendingImages = async () => {
    if (!window.confirm('画像送信を停止しますか？\n\n以降、アンケート回答時に画像が送信されなくなります。')) {
      return;
    }

    try {
      await lecture.ref.update({ imagesSendingStarted: false });
      toast.success('画像送信を停止しました');
    } catch (e) {
      console.error(e);
      toast.error('画像送信の停止に失敗しました');
    }
  };

  return (
    <div>
      <div className="admin-event container py-5 position-relative">
        <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
          {lecture?.imagesSendingStarted && (
            <div className="alert alert-success mb-0 py-2 px-3 d-flex align-items-center">
              <span className="fas fa-check-circle mr-2" />
              画像送信開始済み
            </div>
          )}
          <ModalButton
            title="画像"
            label={(_ => _ > 0 ? `画像（${_}枚）` : '画像')((lectureFrameImagesDoc?.images || []).length)}
            content={() => (
              <LectureImagesManager
                event={event}
                lecture={lecture}
                lectureFrames={lectureFrames}
                lectureFrameImagesDoc={lectureFrameImagesDoc}
                lectureFrameImagesRef={lectureFrameImagesRef}
                lectureId={lectureId}
                onStartSendingImages={handleStartSendingImages}
                onStopSendingImages={handleStopSendingImages}
              />
            )}
            modalProps={{ style: { minWidth: '80vw' } }}
          />
        </div>
        <div className="d-flex justify-content-center mb-3">
          <LectureSelector {...{ lectureId, eventProductTypesById, lectureTypesById, lectures }} />
        </div>
        <div>
          {lectureFrames.map((lectureFrame, frameIndex) => {
            const { startAt, endAt, capacity = 0, lectureType, pics } = lectureFrame;
            const entryFrames = entryFramesGroupedByFrameIndex[frameIndex] || [];
            const frameImages = (lectureFrameImagesDoc?.images || []).filter(_ => _.frameIndex === frameIndex);

            const onSubmitEditPics = async ({ pics }) => {
              try {
                const frames = [...lectureFrames.slice(0, frameIndex), { ...lectureFrame, pics }, ...lectureFrames.slice(frameIndex + 1)];
                await lecture.ref.update({ frames });
                toast.success('担当者を更新しました');
              } catch(e) {
                console.error(e);
                toast.error('失敗しました');
              }
            };

            return (
              <div key={frameIndex} className="card p-3 my-2">
                <div className="d-flex align-items-center mb-2 gap-2">
                  <div>
                    {formatDate(startAt.toDate(), 'HH:mm')} 〜 {formatDate(endAt.toDate(), 'HH:mm')}
                  </div>
                  <div>{lectureTypesById[lectureType]?.name}</div>
                  <duv>{(pics || []).map(({ picId, staffRole}) => staffs.find(_ => _.id === picId)?.displayName + (staffRole ? `(${staffRoles[staffRole].label})` : '')).join(',')}</duv>
                  <ModalButton Modal={PicsFormModal} size="sm" modalProps={{ pics, staffs, onSubmit: onSubmitEditPics }}>
                    <span className="fas fa-edit" />
                  </ModalButton>
                  <Button size="sm" color='secondary' tag={TenantLink} to={`/events/${event.id}/qrcode?${qs.stringify({ force: 1, lectureId, frameIndex, })}`} target='_blank'>
                    <span className='fas fa-external-link-alt mr-1' />
                    強制申込QR
                  </Button>
                </div>
                <div className="d-flex align-items-center flex-wrap">
                  {range(0, Math.max(capacity, entryFrames.filter(_ => _.entry?.cancelledAt == null).length)).map((seatIndex) => {
                    return (
                      <LectureItem
                        key={seatIndex}
                        {...{ event, entries, lecture, lectureFrame, entryFrames, seatIndex, entryIds, eventProductTypesById, lectureTypesById }}
                        {...props}
                      />
                    );
                  })}
                  {entryFrames
                    .filter((_) => _.entry?.cancelledAt != null)
                    .map((entryFrame, i) => {
                      return <CancelledLectureItem key={i} {...{ event, entries, lecture, entryFrame, entryIds, eventProductTypesById, lectureTypesById, }} {...props} />;
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

function LectureItem({ event, entries, lecture, lectureFrame, entryFrames, seatIndex, entryIds, user, history, eventProductTypesById, lectureTypesById }) {
  const { entryId } = useQueryParams();
  const tenant = useTenant();
  const { name, entry } = entryFrames.filter((_) => _.entry?.cancelledAt == null)[seatIndex] || {};
  const { priorCapacity = 0 } = lectureFrame;
  const { checkedInAt, abortedAt, isTheDay, isTemporary } = entry || {};
  const isPrior = seatIndex < priorCapacity;
  const isCheckedIn = checkedInAt != null;
  const Container = entry ? 'button' : 'span';
  const entryIndex = entries.findIndex(_ => _.id === entry?.id) + 1;
  const imagesOpenedAt = entry?.lectureFrameImagesOpenedAt;

  const onClickTemp = async (e) => {
    e.stopPropagation();
    if(!window.confirm(`キャンセルします。よろしいですか？`)) return;

    try {
      await entry.ref.delete();
      toast.success('キャンセルしました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <>
      <Container
        className={classnames('card p-2 m-1 mb-2 text-center position-relative d-flex flex-column justify-content-center', {
          'border-info': isPrior,
        })}
        style={{ width: 140, height: 77, lineHeight: 1.3 }}
        onClick={() => entry && history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}?entryId=${entry.id}`)}
      >
        <div className="position-absolute" style={{ top: -10, left: 0 }}>
          {entryIndex > 0 && (
            <span className="badge badge-pill badge-info">
              {event?.entryIndexPrefix}{entryIndex}
             </span>
          )}
          {isCheckedIn && (
            <span className="badge badge-pill badge-info">
              チェックイン済
            </span>
          )}
        </div>
        <div className="position-absolute" style={{ bottom: -10, left: 0 }}>
          {isTheDay && (
            <span className="badge badge-pill badge-secondary">
              当日
            </span>
          )}
          {isTemporary && (
            <span className="badge badge-pill badge-secondary cursor-pointer" onClick={onClickTemp}>
              仮押さえ中
            </span>
          )}
          {abortedAt != null && (
            <span className="badge badge-pill badge-secondary">
              中止済み
            </span>
          )}
          {imagesOpenedAt != null && (
            <span className="badge badge-pill badge-success" title={`画像閲覧: ${formatDate(imagesOpenedAt.toDate(), 'yyyy/MM/dd HH:mm')}`}>
              <span className="fas fa-image mr-1" />
              閲覧済
            </span>
          )}
        </div>
        <span className="mt-1" style={{ color: entry ? '#007bff' : '' }}>{name}</span>
        <span className="text-muted small">{entry && entry.createdBy.displayName}</span>
        <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
          {entry && formatDate(entry.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
        </span>
        {entry != null && entry.createdAt.toDate() > addHours(new Date(), -60) && (
          <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
            {
              entry.forceKey && (
                <span className="badge badge-pill badge-secondary mr-1">
                  強制申込
                </span>
              )
            }
            {formatDistanceToNow(entry.createdAt.toDate(), { locale: ja })}前
          </span>
        )}
      </Container>
      {entry != null && (
        <LectureDetailModal
          isOpen={entry.id === entryId}
          onClickClose={() => history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}`)}
          {...{ event, user, entry, lecture, entryIds, history, eventProductTypesById, lectureTypesById, }}
        />
      )}
    </>
  );
}

function CancelledLectureItem({ event, entries, lecture, entryFrame, entryIds, user, history, eventProductTypesById, lectureTypesById, }) {
  const { entryId } = useQueryParams();
  const tenant = useTenant();
  const { name, entry } = entryFrame;
  const Container = entry ? 'button' : 'span';
  const entryIndex = entries.findIndex(_ => _.id === entry?.id) + 1;
  return (
    <>
      <Container
        className={classnames(
          'card p-2 m-1 text-center position-relative d-flex flex-column justify-content-center bg-light-grey'
        )}
        style={{ width: 140, height: 70, lineHeight: 1.3, opacity: 0.6 }}
        onClick={() => entry && history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}?entryId=${entry.id}`)}
      >
        {entryIndex > 0 && (
          <span className="badge badge-pill badge-info position-absolute" style={{ top: -10, left: 0 }}>
            {event?.entryIndexPrefix}{entryIndex}
           </span>
        )}
        <span className="badge badge-pill badge-secondary position-absolute" style={{ top: -10, left: 30 }}>
          キャンセル済み
        </span>
        <span className="mt-1" style={{ color: entry ? '#007bff' : '' }}>{name}</span>
        <span className="text-muted small">{entry && entry.createdBy.displayName}</span>
        {entry.cancelledAt.toDate() > addHours(new Date(), -60) && (
          <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
            {formatDistanceToNow(entry.cancelledAt.toDate(), { locale: ja })}前
          </span>
        )}
      </Container>
      {entry != null && (
        <LectureDetailModal
          isOpen={entry.id === entryId}
          onClickClose={() => history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}`)}
          {...{ event, user, entry, lecture, entryIds, history, eventProductTypesById, lectureTypesById, }}
        />
      )}
    </>
  );
}